(ns app.front.view.group.admin.integration.common
  (:require [app.domain.group.integration-config-model :as integration-config-model]
            [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema-tools.core :as st]
            [schema.core :as s]))

(rf/reg-event-db ::set-service
  (fn [db [_ new-service]]
    (update-in db [::data :edited-config]
               (fn [{:integration-config/keys [type service] :as old-config}]
                 (if (= service new-service)
                   old-config
                   {:integration-config/type    type
                    :integration-config/service new-service})))))

(rf/reg-event-db ::set-attr
  (fn [db [_ k v]]
    (assoc-in db [::data :edited-config k] v)))

(rf/reg-event-db ::set-deep-attr
  (fn [db [_ ks v]]
    (assoc-in db (concat [::data :edited-config] ks) v)))

(rf/reg-event-db ::dissoc-deep-attr
  (fn [db [_ ks]]
    (p/dissoc-in db (concat [::data :edited-config] ks))))

(rf/reg-event-db ::start-editing
  (fn [db [_ integration-type current-config]]
    (cond-> (-> (assoc-in db [::data ::editing?] true)
                (p/dissoc-in [::data ::connection-test])
                (assoc-in [::data :edited-config]
                          (or (some-> current-config
                                      eu/->map
                                      (st/select-schema integration-config-model/IntegrationConfigBase))
                              {:integration-config/type integration-type})))
      current-config (assoc-in [::data :stored-config] current-config))))

(rf/reg-event-db ::cancel-editing
  (fn [db]
    (dissoc db ::data)))

(rf/reg-event-fx ::save-config
  (fn [{:keys [db]} [_ group-id command]]
    (let [data (-> db ::data :edited-config)]
      {:comm/command {:command    command
                      :data       (merge {:group-id group-id}
                                         (if (:integration-config/service data)
                                           {:data data}
                                           {:remove? true}))
                      :notify     {:success :changes-saved}
                      :on-success [::save-config.success]}})))

(rf/reg-event-fx ::save-config.success
  (fn [_ [_ status resp]]
    {:fx [[:dispatch [::cancel-editing]]
          [:dispatch [::entities/apply-changes status resp]]]}))

(rf/reg-event-fx ::test-connection
  (fn [{:keys [db]} [_ group-id integration-type]]
    {:db           (assoc-in db [::data ::connection-test integration-type] {:loading? true})
     :comm/command {:command    :group/test-integration-connection
                    :data       (util/mmap {:integration-config/type integration-type}
                                           group-id)
                    :on-success [::test-connection.success integration-type]}}))

(rf/reg-event-db ::test-connection.success
  (fn [db [_ integration-type _ response]]
    (assoc-in db [::data ::connection-test integration-type] response)))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::editing?
  :<- [::data]
  (fn [data _]
    (::editing? data false)))

(rf/reg-sub ::stored-config
  :<- [::data]
  :-> :stored-config)

(rf/reg-sub ::edited-config
  :<- [::data]
  :-> :edited-config)

(rf/reg-sub ::modified?
  :<- [::stored-config]
  :<- [::edited-config]
  (fn [[stored-config edited-config] _]
    (and edited-config (not= edited-config stored-config))))

(rf/reg-sub ::edit-valid?
  :<- [::edited-config]
  (fn [{:integration-config/keys [service] :as edited-config} _]
    (and edited-config
         (or (nil? service)
             (-> integration-config-model/IntegrationConfig
                 (s/check edited-config)
                 ;; allow missing client-secret - the existing value will be used if it's undefined
                 (dissoc :integration-config/client-secret
                         :integration-config/private-key)
                 empty?)))))

(rf/reg-sub ::displayed-config
  (fn [[_ stored-config-subscription]]
    [(rf/subscribe stored-config-subscription)
     (rf/subscribe [::edited-config])
     (rf/subscribe [::gas/group])])
  (fn [[stored-config edited-config group] _]
    (let [config (or edited-config stored-config)]
      (when config
        (let [{:integration-config/keys [service type]} config]
          ;; For plan-review, validate that the service matches the enabled feature
          (if (= type :integration-config.type/plan-review)
            (case service
              :integration-config.service/bluebeam
              (when (group-model/has-feature? group :group.feature/enable-bluebeam)
                config)
              :integration-config.service/digeplan
              (when (group-model/has-feature? group :group.feature/external-plan-review-integration)
                config)
              ;; No service selected or unknown service
              config)
            ;; Non-plan-review configs pass through
            config))))))

(rf/reg-sub ::supported-services
  :<- [:session/admin?]
  :<- [:session/user]
  :<- [::stored-config]
  :<- [::gas/group]
  (fn [[is-admin?
        user
        {current-service :integration-config/service}
        group]
       [_ integration-type]]
    (let [services           (-> integration-config-model/integration-configs-by-service
                                 (get integration-type)
                                 keys)
          available-services (if (= integration-type :integration-config.type/plan-review)
                               (filter (fn [service]
                                         (case service
                                           :integration-config.service/bluebeam
                                           (and (group-model/has-feature? group :group.feature/enable-bluebeam)
                                                (or (:user/bluebeam-integration? user)
                                                    is-admin?))
                                           ;; digeplan uses the general feature
                                           (group-model/has-feature? group :group.feature/external-plan-review-integration)))
                                       services)
                               services)]
      (cond->> available-services
        ;; only admin can configure exchange - needs siirappi config
        (not is-admin?) (remove (fn [service]
                                  (and (not= service current-service)
                                       (contains? integration-config-model/admin-only-services service))))))))


(rf/reg-sub ::connection-test-result
  :<- [::data]
  (fn [data [_ integration-type]]
    (get-in data [::connection-test integration-type])))

(defn- applicable-config-schema [type service]
  (some-> integration-config-model/integration-configs-by-service
          (get type)
          (get service)
          st/required-keys))


(defn connection-test-section [{:keys [group-id integration-type tr]}]
  [:div.mt-4
   (cc/button {:kind     :tertiary
               :icon     i/refresh
               :on-click #(>evt [::test-connection group-id integration-type])}
              (tr [:integration-config/test-connection]))
   (when-let [result (<sub [::connection-test-result integration-type])]
     (if (:loading? result)
       [:div.mt-4 (cc/inline-loading {:description (tr [:integration-config/testing])})]
       (let [{:keys [ok status response]} result]
         [:div.mt-4.bg-ui-light-gray.rounded.p-4
          [:table
           [:tbody
            [:tr
             [:td.font-semibold.p-2 (tr [:gis-server-config/status])]
             [:td.p-2 [ui/pill {:color (if ok :green :red)
                                :size  :small
                                :text  (if ok
                                         (tr [:ok])
                                         (str (tr [:error/error]) " " status))}]]]
            (if (map? response)
              (for-frag [[k v] response]
                [:tr
                 [:td.font-semibold.p-2 k]
                 [:td.p-2 (pr-str v)]])
              [:tr
               [:td.font-semibold.p-2 (tr [:gis-server-config/message])]
               [:td.p-2 (if (string? response) response (pr-str response))]])]]])))])

(defn generic-editor [{:keys [tr attr-editors]} {:integration-config/keys [type service] :as config}]
  (let [tr-silent (<sub [:tr/quiet])]
    (for-frag [[k v] (applicable-config-schema type service)
               :let [current-value (get config k)]]
      ^{:key (str k)}
      [:div.mt-3
       (if-let [special-editor (get attr-editors k)]
         [special-editor (util/mmap {:editing? true} tr) config]
         (if (= v s/Bool)
           (cc/toggle {:toggled    (boolean current-value)
                       :id         (name k)
                       :label-text (tr [k])
                       :labelA     (tr [:no])
                       :labelB     (tr [:yes])
                       :on-toggle  #(>evt [::set-attr k %])})
           [ui/text-input (merge {:id         (name k)
                                  :label-text (tr [k])
                                  :required   true
                                  :light      true
                                  :value      (or current-value "")
                                  :on-change  #(rf/dispatch-sync [::set-attr k (futil/tval %)])}
                                 (when-let [help (-> (str "integration-config.help." (name type) "." (name service))
                                                     (keyword (name k))
                                                     vector
                                                     tr-silent)]
                                   {:helper-text help})
                                 (when (#{:integration-config/client-secret
                                          :integration-config/private-key} k)
                                   {:auto-complete "off"}))]))])))

(defn config-editor-page
  "Create a generic interface config editor with custom inputs for actual configuration.

   :schema - a map-type schema so that it can be iterated with schema-tools
   :stored-config-subscription - a subscription vector to get the current config in datebase
   :supported-types-subscription - a subs vector for a sequence of allowed type keywords
   :config-editor - the component that implements editing the configuration
   :attr-editors - map of {:attr-keyword editor+viewer-component-ref}
   :save-command - the backend command that is used to save the value"
  [{:keys [tr
           integration-type
           group-id
           stored-config-subscription
           save-command
           config-editor
           attr-editors]}]
  (let [{:integration-config/keys [service]
         :as                      config}   (<sub [::displayed-config stored-config-subscription])
        editing?                            (<sub [::editing?])
        editor                              (or config-editor generic-editor)]
    [section/section
     "integration-config"
     {:tr             tr
      :heading-button (cc/button
                        {:size             :small
                         :kind             :ghost
                         :icon-description ""
                         :disabled         editing?
                         :on-click         #(>evt [::start-editing integration-type config])}
                        (tr [:edit]))}
     integration-type
     [(if editing? ui-add/add-widget :div)
      (when editing?
        {:buttons [{:text     (tr [:cancel])
                    :on-click #(>evt [::cancel-editing])}
                   {:text      (tr [:save])
                    :disabled? (or (not (<sub [::modified?]))
                                   (not (<sub [::edit-valid?])))
                    :on-click  #(>evt [::save-config group-id save-command])}]})
      [:div.flex.flex-col
       {:style {:max-width "1000px"}}
       (if editing?
         [ui-select/select {:values       (<sub [::supported-services integration-type])
                            :allow-empty? true
                            :val->str     (comp tr vector)
                            :label-text   (tr [:integration-config/service])
                            :empty-text   (tr [:integration-config/disabled])
                            :light        true
                            :id           "integration-service"
                            :class-name   "full-width-select"
                            :on-change    #(>evt [::set-service %])
                            :value        service}]
         [acommon/form-item tr :integration-config/service (tr [(or service
                                                                    (tr [:integration-config/disabled]))])])
       (if editing?
         [editor (util/mmap tr attr-editors) config]
         (for-frag [[k v] (applicable-config-schema integration-type service)
                    :let [special-editor (get attr-editors k)]]
           (if special-editor
             [special-editor (util/mmap tr editing?) config]
             (case k
               :integration-config/client-secret
               [acommon/form-item tr k [:span.text-ui-dark-gray (tr [:integration-config/hidden])]]
               :integration-config/private-key
               [acommon/form-item tr k [:span.text-ui-dark-gray (tr [:integration-config/hidden])]]
               [acommon/form-item tr k (if (= v s/Bool)
                                         (tr [(if (get config k) :yes :no)])
                                         (-> config (get k "") str))]))))
       (when (and (not editing?)
                  service
                  (integration-config-model/type-supports-connection-check? integration-type))
         [connection-test-section (util/mmap group-id integration-type tr)])]]]))
